'use client';

import { HistoryCachedImage } from '@/cms/components/blocks/workspace/workspace-1/shared/history/history-cached-image';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import {
  ChevronLeft,
  ChevronRight,
  ImageUp,
  Play,
  X,
} from '@/components/icons/app-icons';
import { PremiumCrownIcon } from '@/components/paid-access/premium-crown-icon';
import { Spinner } from '@/components/ui/feedback/status/spinner';
import { CachedWsrvImage } from '@/components/ui/media/cached-wsrv-image';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/overlay/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/overlay/drawer';
import { cn } from '@/lib/ui/utils';
import { useReducedMotion } from 'motion/react';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  WorkspaceToolAccessPolicyRule,
  WorkspaceToolParamValue,
  WorkspaceToolTemplatePickerView,
} from '../contracts/tool-view';
import type {
  WorkspaceAssetTarget,
  WorkspaceStudioViewModel,
} from '../contracts/view-model';
import { useWorkspaceUploadDrop } from './use-workspace-upload-drop';
import { WorkspaceAssetsTrigger } from './workspace-assets-trigger';
import { WorkspaceInfoPopover } from './workspace-info-popover';
import { WorkspaceInfoTooltip } from './workspace-info-tooltip';

const WORKSPACE_TEMPLATE_INLINE_CARD_LIMIT = 8;

type TemplatePickerGroup = WorkspaceToolTemplatePickerView['groups'][number];
type TemplatePickerTemplate = TemplatePickerGroup['templates'][number];

function handleTemplateGroupTabKeyDown(params: {
  event: ReactKeyboardEvent<HTMLButtonElement>;
  currentGroupId: string;
  groups: Array<{ id: string }>;
  onSelect: (groupId: string) => void;
}) {
  const { event, currentGroupId, groups, onSelect } = params;
  const currentIndex = groups.findIndex((group) => group.id === currentGroupId);
  if (currentIndex < 0 || groups.length < 2) return;

  const isRtl = getComputedStyle(event.currentTarget).direction === 'rtl';
  let nextIndex: number;
  if (
    event.key === (isRtl ? 'ArrowLeft' : 'ArrowRight') ||
    event.key === 'ArrowDown'
  ) {
    nextIndex = (currentIndex + 1) % groups.length;
  } else if (
    event.key === (isRtl ? 'ArrowRight' : 'ArrowLeft') ||
    event.key === 'ArrowUp'
  ) {
    nextIndex = (currentIndex - 1 + groups.length) % groups.length;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = groups.length - 1;
  } else {
    return;
  }

  event.preventDefault();
  onSelect(groups[nextIndex]!.id);
  const tabs = event.currentTarget
    .closest('[role="tablist"]')
    ?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)');
  tabs?.[nextIndex]?.focus();
}

/** Shared mode tabs for Workspace parameters and template groups. */
export function WorkspaceModeTabs({
  label,
  items,
  selectedId,
  onSelect,
  previousLabel,
  nextLabel,
  className,
}: {
  label: string;
  items: Array<{
    id: string;
    label: string;
    disabled?: boolean;
    disabledReason?: string;
    help?: ReactNode;
    thumbnail?: string;
  }>;
  selectedId?: string;
  onSelect: (id: string) => void;
  previousLabel?: string;
  nextLabel?: string;
  className?: string;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const hasThumbnails = items.some((item) => item.thumbnail);
  const [edges, setEdges] = useState({ left: false, right: false });
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !hasThumbnails) return;
    const update = () => {
      const maximum = Math.max(0, list.scrollWidth - list.clientWidth);
      const offset =
        getComputedStyle(list).direction === 'rtl'
          ? maximum + list.scrollLeft
          : list.scrollLeft;
      const left = maximum > 1 && offset > 1;
      const right = maximum > 1 && maximum - offset > 1;
      setEdges((current) =>
        current.left === left && current.right === right
          ? current
          : { left, right }
      );
    };
    update();
    list.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    observer.observe(list);
    return () => {
      list.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [hasThumbnails, items]);
  const scroll = (direction: -1 | 1) => {
    const list = listRef.current;
    if (!list) return;
    list.scrollBy({
      left: direction * list.clientWidth,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };
  const tabs = (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      data-scroll-left={hasThumbnails && edges.left ? 'true' : undefined}
      data-scroll-right={hasThumbnails && edges.right ? 'true' : undefined}
      className={cn(
        hasThumbnails
          ? 'cms-workspace-mode-thumbnails'
          : 'flex w-full min-w-0 gap-1 overflow-x-auto rounded-pill bg-muted p-1',
        className
      )}
    >
      {items.map((item) => (
        <div
          key={item.id}
          data-disabled={item.disabled ? 'true' : undefined}
          className={
            item.thumbnail
              ? 'cms-workspace-mode-thumbnails__item'
              : item.help || item.disabledReason
                ? 'flex shrink-0 items-center gap-1'
                : 'flex min-w-0 flex-1 items-center gap-1'
          }
        >
          <button
            key={item.id}
            type="button"
            role="tab"
            disabled={item.disabled}
            aria-selected={item.id === selectedId}
            tabIndex={item.id === selectedId ? 0 : -1}
            title={item.label}
            aria-label={item.label}
            className={cn(
              item.thumbnail
                ? 'cms-workspace-mode-thumbnails__button'
                : 'min-w-0 flex-1 rounded-pill px-3 py-1.5 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/60 disabled:opacity-50',
              item.id === selectedId
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => onSelect(item.id)}
            onKeyDown={(event) =>
              handleTemplateGroupTabKeyDown({
                event,
                currentGroupId: item.id,
                groups: items.filter((candidate) => !candidate.disabled),
                onSelect,
              })
            }
          >
            {!item.thumbnail ? (
              <span className="block truncate">{item.label}</span>
            ) : null}
          </button>
          {item.thumbnail ? (
            <>
              <div className="cms-workspace-mode-thumbnails__media">
                <CachedWsrvImage
                  showCheckerboard
                  loadingSkeleton
                  src={item.thumbnail}
                  alt={item.label}
                  width={512}
                  height={512}
                  loading="lazy"
                  decoding="async"
                />
                {item.disabledReason ? (
                  <WorkspaceInfoTooltip
                    className="cms-workspace-mode-thumbnails__disabledReason"
                    icon={renderAppIcon({
                      name: 'forbid-circle',
                      className:
                        'cms-workspace-mode-thumbnails__disabledReasonIcon',
                    })}
                    text={item.disabledReason}
                  />
                ) : null}
              </div>
              <div className="cms-workspace-mode-thumbnails__caption">
                <span>{item.label}</span>
                {item.help}
              </div>
            </>
          ) : (
            <>
              {item.help}
              {item.disabledReason ? (
                <WorkspaceInfoTooltip
                  className="cms-block-workspace-workspace-1-workspace-form-controls__paramRestrictionTrigger"
                  icon={renderAppIcon({
                    name: 'forbid-circle',
                    className:
                      'cms-block-workspace-workspace-1-workspace-form-controls__paramRestrictionIcon',
                  })}
                  text={item.disabledReason}
                />
              ) : null}
            </>
          )}
        </div>
      ))}
    </div>
  );
  if (!hasThumbnails) return tabs;

  return (
    <div className="cms-workspace-mode-thumbnails__viewport">
      {tabs}
      {previousLabel ? (
        <button
          type="button"
          className="cms-workspace-mode-thumbnails__navigation cms-workspace-mode-thumbnails__navigation--left"
          aria-label={previousLabel}
          aria-hidden={!edges.left}
          data-visible={edges.left ? 'true' : 'false'}
          disabled={!edges.left}
          tabIndex={edges.left ? undefined : -1}
          title={previousLabel}
          onClick={() => scroll(-1)}
        >
          <ChevronLeft aria-hidden="true" />
        </button>
      ) : null}
      {nextLabel ? (
        <button
          type="button"
          className="cms-workspace-mode-thumbnails__navigation cms-workspace-mode-thumbnails__navigation--right"
          aria-label={nextLabel}
          aria-hidden={!edges.right}
          data-visible={edges.right ? 'true' : 'false'}
          disabled={!edges.right}
          tabIndex={edges.right ? undefined : -1}
          title={nextLabel}
          onClick={() => scroll(1)}
        >
          <ChevronRight aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

type WorkspaceTemplatePickerLabels = Pick<
  WorkspaceStudioViewModel['shell']['controls'],
  | 'assetsLabel'
  | 'clearHistorySelectionLabel'
  | 'premiumFeatureTooltip'
  | 'toolCatalogNextPageLabel'
  | 'toolCatalogPreviousPageLabel'
  | 'toolPickerCloseLabel'
  | 'uploadRemoveImageLabel'
  | 'uploadTileLabel'
> &
  NonNullable<
    WorkspaceStudioViewModel['shell']['controls']['templatePickerCopy']
  >;

export function resolveWorkspaceInlineTemplates(params: {
  group: TemplatePickerGroup;
  selectedValue: string | null;
}) {
  const officialLimit = Math.max(
    0,
    WORKSPACE_TEMPLATE_INLINE_CARD_LIMIT - (params.group.customImage ? 1 : 0)
  );
  const initial = params.group.templates.slice(0, officialLimit);
  const selected = params.selectedValue
    ? params.group.templates.find(
        (template) => template.value === params.selectedValue
      )
    : undefined;

  if (
    !selected ||
    initial.some((template) => template.value === selected.value)
  ) {
    return initial;
  }

  return [
    selected,
    ...params.group.templates.filter(
      (template) => template.value !== selected.value
    ),
  ].slice(0, officialLimit);
}

function readStringParam(
  values: Record<string, WorkspaceToolParamValue>,
  fieldId: string
) {
  const value = values[fieldId];
  return typeof value === 'string' && value.trim() ? value : null;
}

function WorkspaceTemplatePreviewContent({
  template,
  reduceMotion,
  loadErrorLabel,
}: {
  template: TemplatePickerTemplate;
  reduceMotion: boolean;
  loadErrorLabel: string;
}) {
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => setLoadFailed(false), [template.animatedPreviewUrl]);
  if (!template.animatedPreviewUrl) {
    throw new Error(
      `Workspace template ${template.value} has no animated preview URL.`
    );
  }

  return (
    <div className="grid gap-3">
      <div className="relative aspect-video overflow-hidden rounded-control bg-muted/30">
        {loadFailed ? (
          <p
            className="flex size-full items-center justify-center px-4 text-center text-sm text-destructive"
            role="alert"
          >
            {loadErrorLabel}
          </p>
        ) : reduceMotion ? (
          <HistoryCachedImage
            loadingSkeleton
            src={template.previewUrl}
            alt={template.label}
            className="size-full object-cover"
            loading="eager"
            decoding="async"
            widths={[256, 384, 512]}
            sizes="384px"
            quality={80}
          />
        ) : (
          <CachedWsrvImage
            showCheckerboard
            loadingSkeleton
            animated
            src={template.animatedPreviewUrl}
            alt={template.label}
            className="size-full object-cover"
            decoding="async"
            onError={() => setLoadFailed(true)}
          />
        )}
      </div>
      {template.infoText ? (
        <p className="text-sm leading-relaxed text-muted-foreground">
          {template.infoText}
        </p>
      ) : null}
    </div>
  );
}

function WorkspaceTemplateCard({
  group,
  template,
  selected,
  showPaidFeatureMarkers,
  premiumFeatureTooltip,
  surface,
  onSelect,
  onPreview,
  reduceMotion,
  labels,
}: {
  group: TemplatePickerGroup;
  template: TemplatePickerTemplate;
  selected: boolean;
  showPaidFeatureMarkers: boolean;
  premiumFeatureTooltip: string;
  surface: 'inline' | 'dialog';
  onSelect: () => void;
  onPreview: () => void;
  reduceMotion: boolean;
  labels: WorkspaceTemplatePickerLabels;
}) {
  const canPreview = Boolean(template.animatedPreviewUrl);

  return (
    <div
      className="group/template-preview relative min-w-0"
      data-template-preview-card="true"
    >
      <label
        className={cn(
          'group relative isolate flex min-w-0 flex-col overflow-hidden rounded-floating border bg-muted/10 text-left shadow-sm outline-none transition-[border-color,box-shadow,transform] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/60',
          selected
            ? 'border-primary ring-1 ring-primary/70'
            : 'border-border/60 hover:-translate-y-0.5 hover:border-foreground/30',
          template.disabled && 'cursor-not-allowed opacity-50'
        )}
        data-template-group-id={group.id}
        data-template-value={template.value}
        data-template-surface={surface}
      >
        <input
          type="radio"
          className="peer sr-only"
          name={`workspace-template-${surface}-${group.id}`}
          value={template.value}
          checked={selected}
          disabled={template.disabled}
          aria-label={template.label}
          onChange={onSelect}
        />
        <span className="relative block aspect-square w-full overflow-hidden bg-muted/20">
          <HistoryCachedImage
            loadingSkeleton
            src={template.previewUrl}
            alt={template.label}
            className="size-full object-cover transition-transform group-hover:scale-[1.02]"
            loading="lazy"
            decoding="async"
            sizes="(max-width: 640px) 40vw, 128px"
            widths={[96, 128, 192, 256]}
            quality={75}
          />
          {showPaidFeatureMarkers && template.requiredPaid ? (
            <PremiumCrownIcon
              className="absolute right-1.5 top-1.5 rounded-pill bg-background/85 p-1 shadow-sm backdrop-blur-sm"
              iconClassName="size-3.5"
              tooltipText={premiumFeatureTooltip}
            />
          ) : null}
        </span>
        <span className="min-w-0 px-2 py-2 text-xs font-medium text-foreground">
          <span className="block truncate">{template.label}</span>
        </span>
      </label>

      {canPreview ? (
        <span className="absolute bottom-1 right-1 hidden md:inline-flex">
          <WorkspaceInfoPopover
            label={`${labels.templatePreviewLabel}: ${template.label}`}
          >
            <WorkspaceTemplatePreviewContent
              template={template}
              reduceMotion={reduceMotion}
              loadErrorLabel={labels.templatePreviewLoadError}
            />
          </WorkspaceInfoPopover>
        </span>
      ) : null}

      {canPreview ? (
        <button
          type="button"
          className="absolute bottom-9 right-1.5 z-10 flex size-8 items-center justify-center rounded-pill border border-border/60 bg-background/90 text-foreground shadow-sm outline-none backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-ring/60 md:hidden"
          aria-label={`${labels.templatePreviewLabel}: ${template.label}`}
          title={labels.templatePreviewLabel}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onPreview();
          }}
        >
          <Play className="size-4" />
        </button>
      ) : null}
    </div>
  );
}

function WorkspaceCustomTemplateCard({
  group,
  cachedUrl,
  selected,
  uploading,
  assetsOpen,
  labels,
  showPaidFeatureMarkers,
  surface,
  onChooseFile,
  onOpenAssets,
  onRequestAccess,
  onRemove,
  onSelect,
}: {
  group: TemplatePickerGroup;
  cachedUrl: string | null;
  selected: boolean;
  uploading: boolean;
  assetsOpen: boolean;
  labels: WorkspaceTemplatePickerLabels;
  showPaidFeatureMarkers: boolean;
  surface: 'inline' | 'dialog';
  onChooseFile: (file: File) => void;
  onOpenAssets: (target: WorkspaceAssetTarget) => void;
  onRequestAccess: (policy: WorkspaceToolAccessPolicyRule) => boolean;
  onRemove: () => void;
  onSelect: () => void;
}) {
  const customImage = group.customImage;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const uploadDrop = useWorkspaceUploadDrop((files) => {
    if (files[0]) {
      onChooseFile(files[0]);
    }
  }, uploading || !customImage);
  if (!customImage) return null;

  const openFilePicker = () => {
    inputRef.current?.click();
  };
  const openAssets = () => {
    if (onRequestAccess(customImage.accessPolicy)) {
      onOpenAssets({ kind: 'templateCustom', groupId: group.id });
    }
  };

  return (
    <div
      className={cn(
        'group relative isolate flex min-w-0 flex-col overflow-hidden rounded-floating border bg-muted/10 shadow-sm transition-[border-color,box-shadow,transform]',
        selected
          ? 'border-primary ring-1 ring-primary/70'
          : 'border-border/60 hover:-translate-y-0.5 hover:border-foreground/30'
      )}
      data-template-group-id={group.id}
      data-template-kind="custom"
      data-template-surface={surface}
      {...uploadDrop}
    >
      <label className="relative flex aspect-square w-full items-center justify-center overflow-hidden bg-muted/20 outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-ring/60">
        <input
          type="radio"
          className="peer sr-only"
          name={`workspace-template-${surface}-${group.id}`}
          value="custom"
          checked={selected}
          aria-label={customImage.label}
          onChange={cachedUrl ? onSelect : openFilePicker}
        />
        {cachedUrl ? (
          <HistoryCachedImage
            loadingSkeleton
            src={cachedUrl}
            alt={customImage.label}
            className="size-full object-cover"
            loading="lazy"
            decoding="async"
            sizes="(max-width: 640px) 40vw, 128px"
            widths={[96, 128, 192, 256]}
            quality={75}
          />
        ) : (
          <span className="flex flex-col items-center gap-1.5 px-2 text-center text-muted-foreground">
            <ImageUp className="size-5" />
            <span className="text-xs font-medium text-foreground">
              {labels.uploadTileLabel}
            </span>
          </span>
        )}
        {uploading ? (
          <span className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm">
            <Spinner className="size-5" />
          </span>
        ) : null}
      </label>

      <span className="flex min-w-0 items-center gap-1 px-2 py-2 text-xs font-medium text-foreground">
        <span className="min-w-0 flex-1 truncate">{customImage.label}</span>
        {showPaidFeatureMarkers && customImage.requiredPaid ? (
          <PremiumCrownIcon
            className="shrink-0"
            iconClassName="size-3.5"
            tooltipText={labels.premiumFeatureTooltip}
          />
        ) : null}
      </span>

      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        accept={customImage.allowedMimeTypes.join(',')}
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onChooseFile(file);
        }}
      />

      <div className="absolute left-1.5 top-1.5">
        <WorkspaceAssetsTrigger
          label={labels.assetsLabel}
          open={assetsOpen}
          placement="upload-corner"
          onClick={openAssets}
        />
      </div>

      {cachedUrl ? (
        <button
          type="button"
          aria-label={labels.uploadRemoveImageLabel}
          title={labels.uploadRemoveImageLabel}
          className="absolute right-1.5 top-1.5 flex size-7 items-center justify-center rounded-pill border border-border/60 bg-background/90 text-foreground shadow-sm outline-none backdrop-blur-sm hover:bg-background focus-visible:ring-2 focus-visible:ring-ring/60"
          onClick={onRemove}
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export function WorkspaceTemplatePicker({
  toolId,
  picker,
  values,
  labels,
  assetsOpen,
  showPaidFeatureMarkers,
  onChange,
  onOpenAssets,
  onRequestAccess,
  onUploadCustomImage,
}: {
  toolId: string;
  picker: WorkspaceToolTemplatePickerView;
  values: Record<string, WorkspaceToolParamValue>;
  labels: WorkspaceTemplatePickerLabels;
  assetsOpen: boolean;
  showPaidFeatureMarkers: boolean;
  onChange: (fieldId: string, value: WorkspaceToolParamValue) => void;
  onOpenAssets: (target: WorkspaceAssetTarget) => void;
  onRequestAccess: (policy: WorkspaceToolAccessPolicyRule) => boolean;
  onUploadCustomImage: (groupId: string, file: File) => Promise<boolean>;
}) {
  const [activeGroupId, setActiveGroupId] = useState(
    picker.groups[0]?.id ?? ''
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mobilePreview, setMobilePreview] = useState<{
    groupId: string;
    templateValue: string;
  } | null>(null);
  const [dialogGroupId, setDialogGroupId] = useState(activeGroupId);
  const [uploadingGroupId, setUploadingGroupId] = useState<string | null>(null);
  const [customCacheByGroupId, setCustomCacheByGroupId] = useState<
    Record<string, string>
  >(() =>
    Object.fromEntries(
      picker.groups.flatMap((group) => {
        const fieldId = group.customImage?.fieldId;
        const url = fieldId ? readStringParam(values, fieldId) : null;
        return url ? [[group.id, url]] : [];
      })
    )
  );

  useEffect(() => {
    setCustomCacheByGroupId((current) => {
      let changed = false;
      const next = { ...current };
      for (const group of picker.groups) {
        const fieldId = group.customImage?.fieldId;
        const url = fieldId ? readStringParam(values, fieldId) : null;
        if (url && next[group.id] !== url) {
          next[group.id] = url;
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [picker.groups, values]);

  const groupById = useMemo(
    () => new Map(picker.groups.map((group) => [group.id, group])),
    [picker.groups]
  );
  const activeGroup = groupById.get(activeGroupId) ?? picker.groups[0];
  const dialogGroup = groupById.get(dialogGroupId) ?? activeGroup;
  const mobilePreviewGroup = mobilePreview
    ? groupById.get(mobilePreview.groupId)
    : undefined;
  const mobilePreviewTemplate = mobilePreviewGroup?.templates.find(
    (template) => template.value === mobilePreview?.templateValue
  );
  const reduceMotion = useReducedMotion() === true;
  if (!activeGroup) return null;

  const selectOfficial = (
    group: TemplatePickerGroup,
    template: TemplatePickerTemplate
  ) => {
    if (template.disabled || !onRequestAccess(template.accessPolicy)) {
      return false;
    }
    onChange(group.selectionFieldId, template.value);
    return true;
  };
  const selectCustom = (group: TemplatePickerGroup) => {
    const customImage = group.customImage;
    const cachedUrl = customCacheByGroupId[group.id];
    if (
      !customImage ||
      !cachedUrl ||
      !onRequestAccess(customImage.accessPolicy)
    ) {
      return;
    }
    onChange(customImage.fieldId, cachedUrl);
    onChange(group.selectionFieldId, '');
  };
  const clearGroup = (group: TemplatePickerGroup) => {
    onChange(group.selectionFieldId, '');
    if (group.customImage) {
      onChange(group.customImage.fieldId, '');
    }
  };
  const removeCustom = (group: TemplatePickerGroup) => {
    const customImage = group.customImage;
    if (!customImage) return;
    const wasSelected =
      !readStringParam(values, group.selectionFieldId) &&
      Boolean(readStringParam(values, customImage.fieldId));
    setCustomCacheByGroupId((current) => {
      const { [group.id]: _removed, ...next } = current;
      return next;
    });
    onChange(customImage.fieldId, '');
    if (wasSelected) {
      const firstAvailable = group.templates.find(
        (template) => !template.disabled
      );
      onChange(
        group.selectionFieldId,
        group.required && firstAvailable ? firstAvailable.value : ''
      );
    }
  };
  const uploadCustom = async (group: TemplatePickerGroup, file: File) => {
    const customImage = group.customImage;
    if (!customImage || uploadingGroupId) {
      return;
    }
    setUploadingGroupId(group.id);
    try {
      await onUploadCustomImage(group.id, file);
    } finally {
      setUploadingGroupId(null);
    }
  };
  const openTemplateDialog = () => {
    setDialogGroupId(activeGroup.id);
    setDialogOpen(true);
  };

  const renderCards = (
    group: TemplatePickerGroup,
    templates: TemplatePickerTemplate[],
    surface: 'inline' | 'dialog'
  ) => {
    const officialValue = readStringParam(values, group.selectionFieldId);
    const customFieldId = group.customImage?.fieldId;
    const activeCustomUrl = customFieldId
      ? readStringParam(values, customFieldId)
      : null;
    const customSelected = !officialValue && Boolean(activeCustomUrl);

    return (
      <div
        role="radiogroup"
        aria-label={group.label}
        className={cn(
          'grid gap-2',
          surface === 'inline'
            ? 'grid-cols-4'
            : 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-5'
        )}
      >
        {group.customImage ? (
          <WorkspaceCustomTemplateCard
            group={group}
            cachedUrl={customCacheByGroupId[group.id] ?? null}
            selected={customSelected}
            uploading={uploadingGroupId === group.id}
            assetsOpen={assetsOpen}
            labels={labels}
            showPaidFeatureMarkers={showPaidFeatureMarkers}
            surface={surface}
            onChooseFile={(file) => void uploadCustom(group, file)}
            onOpenAssets={onOpenAssets}
            onRequestAccess={onRequestAccess}
            onRemove={() => removeCustom(group)}
            onSelect={() => selectCustom(group)}
          />
        ) : null}
        {templates.map((template) => (
          <WorkspaceTemplateCard
            key={template.value}
            group={group}
            template={template}
            selected={officialValue === template.value}
            showPaidFeatureMarkers={showPaidFeatureMarkers}
            premiumFeatureTooltip={labels.premiumFeatureTooltip}
            surface={surface}
            onSelect={() => selectOfficial(group, template)}
            onPreview={() =>
              setMobilePreview({
                groupId: group.id,
                templateValue: template.value,
              })
            }
            reduceMotion={reduceMotion}
            labels={labels}
          />
        ))}
      </div>
    );
  };

  const activeOfficialValue = readStringParam(
    values,
    activeGroup.selectionFieldId
  );
  const inlineTemplates = resolveWorkspaceInlineTemplates({
    group: activeGroup,
    selectedValue: activeOfficialValue,
  });
  const activeTotalCards =
    activeGroup.templates.length + (activeGroup.customImage ? 1 : 0);

  return (
    <section className="space-y-3" data-workspace-template-picker={toolId}>
      {picker.groups.length > 1 ? (
        <WorkspaceModeTabs
          label={activeGroup.label}
          items={picker.groups}
          selectedId={activeGroup.id}
          onSelect={setActiveGroupId}
          previousLabel={labels.toolCatalogPreviousPageLabel}
          nextLabel={labels.toolCatalogNextPageLabel}
        />
      ) : null}

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground">
            <span className="truncate">{activeGroup.label}</span>
            {activeGroup.required ? (
              <span aria-hidden="true" className="text-destructive">
                *
              </span>
            ) : null}
            {activeGroup.infoText ? (
              <WorkspaceInfoTooltip text={activeGroup.infoText} />
            ) : null}
          </span>
          {!activeGroup.required &&
          (activeOfficialValue ||
            (activeGroup.customImage &&
              readStringParam(values, activeGroup.customImage.fieldId))) ? (
            <button
              type="button"
              className="shrink-0 rounded-compact px-2 py-1 text-xs text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/60"
              onClick={() => clearGroup(activeGroup)}
            >
              {labels.clearHistorySelectionLabel}
            </button>
          ) : null}
        </div>

        {renderCards(activeGroup, inlineTemplates, 'inline')}

        {activeTotalCards > WORKSPACE_TEMPLATE_INLINE_CARD_LIMIT ? (
          <button
            type="button"
            className="w-full rounded-compact border border-border/60 bg-muted/10 px-3 py-2 text-sm font-medium text-muted-foreground outline-none hover:bg-muted/40 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/60"
            aria-label={`${activeGroup.label} (${activeTotalCards})`}
            aria-haspopup="dialog"
            onClick={openTemplateDialog}
          >
            +{activeTotalCards - WORKSPACE_TEMPLATE_INLINE_CARD_LIMIT}
          </button>
        ) : null}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          aria-describedby={undefined}
          positionerClassName="cms-theme"
          className="max-h-[min(88vh,760px)] max-w-[min(94vw,960px)] overflow-hidden p-0 sm:max-w-[min(94vw,960px)]"
        >
          <DialogTitle className="sr-only">{activeGroup.label}</DialogTitle>
          <div className="flex min-h-0 flex-col gap-4 overflow-hidden p-5">
            {picker.groups.length > 1 ? (
              <WorkspaceModeTabs
                label={dialogGroup?.label ?? activeGroup.label}
                items={picker.groups}
                selectedId={dialogGroup?.id}
                onSelect={setDialogGroupId}
                previousLabel={labels.toolCatalogPreviousPageLabel}
                nextLabel={labels.toolCatalogNextPageLabel}
                className="pr-10"
              />
            ) : null}
            {dialogGroup ? (
              <div className="min-h-0 overflow-y-auto pr-1">
                {renderCards(dialogGroup, dialogGroup.templates, 'dialog')}
              </div>
            ) : null}
          </div>
          <span className="sr-only">{labels.toolPickerCloseLabel}</span>
        </DialogContent>
      </Dialog>

      <Drawer
        direction="bottom"
        open={Boolean(mobilePreviewTemplate)}
        onOpenChange={(open) => !open && setMobilePreview(null)}
      >
        {mobilePreviewGroup && mobilePreviewTemplate ? (
          <DrawerContent
            aria-describedby={undefined}
            className="cms-theme max-h-[85vh]"
          >
            <DrawerHeader className="text-left">
              <DrawerTitle>{mobilePreviewTemplate.label}</DrawerTitle>
            </DrawerHeader>
            <div className="min-h-0 overflow-y-auto px-4">
              <WorkspaceTemplatePreviewContent
                template={mobilePreviewTemplate}
                reduceMotion={reduceMotion}
                loadErrorLabel={labels.templatePreviewLoadError}
              />
            </div>
            <DrawerFooter className="grid grid-cols-2">
              <DrawerClose asChild>
                <button
                  type="button"
                  className="rounded-control border px-4 py-2.5 text-sm font-medium outline-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/60"
                >
                  {labels.toolPickerCloseLabel}
                </button>
              </DrawerClose>
              <button
                type="button"
                className="rounded-control bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground outline-none hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-ring/60 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={mobilePreviewTemplate.disabled}
                onClick={() => {
                  if (
                    selectOfficial(mobilePreviewGroup, mobilePreviewTemplate)
                  ) {
                    setMobilePreview(null);
                  }
                }}
              >
                {labels.templateUseLabel}
              </button>
            </DrawerFooter>
          </DrawerContent>
        ) : null}
      </Drawer>
    </section>
  );
}
